import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import QuotationTypeToggle from "@/components/QuotationTypeToggle";
import { useQuotationTypeView, matchesQuotationType } from "@/hooks/useQuotationTypeView";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, X, RefreshCw, Search } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ScatterChart, Scatter, ReferenceLine
} from "recharts";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import { isFreebieSkuId } from "@/lib/freebieCalc";

const ALL_BRANDS = ["Wellcore", "Dynamite", "YouWeFit", "Okami", "OSMO", "Zero Sugar"];

// Brand pill header colors (per spec)
const BRAND_PILLS = {
  "Wellcore":   { bg: "#E1F5EE", color: "#085041" },
  "Dynamite":   { bg: "#FFF0E6", color: "#7A3000" },
  "YouWeFit":   { bg: "#FFFBE6", color: "#7A5C00" },
  "Okami":      { bg: "#FCEBEB", color: "#A32D2D" },
  "OSMO":       { bg: "#E6F1FB", color: "#0C447C" },
  "Zero Sugar": { bg: "#EAF3DE", color: "#27500A" },
  "Unknown":    { bg: "#F1EFE8", color: "#444441" },
};

// Bar color (slightly more vivid than cellColor for chart visibility per spec)
const barColor = (d) => {
  if (d == null) return "#888780";
  if (d >= 66) return "#E24B4A";
  if (d >= 64) return "#D85A30";
  if (d >= 62) return "#BA7517";
  return "#639922";
};

// Bucket color for a discount value (brand-cell color coding per spec)
const cellColor = (d) => {
  if (d == null) return null;
  if (d >= 66) return "#A32D2D"; // red
  if (d >= 64) return "#993C1D"; // coral
  if (d >= 62) return "#BA7517"; // amber
  return "#0F6E56";              // green (below 62%)
};
const needsWarn = (d) => d != null && d >= 62;
const formatCurrency = (v) => { if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`; if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`; if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`; return `₹${v.toLocaleString("en-IN")}`; };

export default function DiscountAnalytics() {
  const [allQuotations, setAllQuotations] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const today = now.toISOString().substring(0,10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [showAllSkus, setShowAllSkus] = useState(false);
  const [skuSearch, setSkuSearch] = useState("");
  const [typeView] = useQuotationTypeView();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true })
      ]);
      setAllQuotations(qRes.data || []);
      setAllSellers(sRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const sellerMap = useMemo(() => { const m = {}; allSellers.forEach(s => { m[s.id] = s; }); return m; }, [allSellers]);
  const toggleBrand = (b) => setSelectedBrands(p => p.includes(b) ? p.filter(x=>x!==b) : [...p, b]);
  const clearFilters = () => { setDateFrom(monthStart); setDateTo(today); setSelectedBrands([]); };
  const hasActiveFilters = dateFrom || dateTo || selectedBrands.length > 0;

  const filteredQuotations = useMemo(() => {
    let f = [...allQuotations];
    f = f.filter(q => !!q.invoice_date);
    if (typeView !== "all") f = f.filter(q => matchesQuotationType(q, typeView));
    if (dateFrom) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") >= dateFrom);
    if (dateTo) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") <= dateTo);
    if (selectedBrands.length > 0) f = f.filter(q => (q.items||[]).some(i => selectedBrands.includes(i.brand)));
    return f;
  }, [allQuotations, dateFrom, dateTo, selectedBrands, typeView]);

  // Per-invoice effective discount % EXCLUDING whitelisted freebie SKUs (Shakers, Carry Bags, etc).
  // Considers all line items (sale + freebie list) but ignores any line whose sku_id is in the
  // admin-managed freebie SKU whitelist. Returns null if no eligible lines exist on the invoice.
  // Formula: Σ(line_mrp − line_invoice_value) / Σ(line_mrp) × 100 across eligible lines.
  const effDiscExclFreebies = (q) => {
    let mrp = 0, disc = 0;
    const accumulate = (it) => {
      if (isFreebieSkuId(it?.sku_id)) return;
      const qty = Number(it?.quantity) || 0;
      const lineMrp = Number(it?.total_mrp) || (Number(it?.mrp) || 0) * qty;
      const lineInvoice = Number(it?.invoice_value) || 0;
      mrp += lineMrp;
      disc += (lineMrp - lineInvoice);
    };
    (q.items || []).forEach(accumulate);
    (q.freebies || []).forEach(accumulate);
    return mrp > 0 ? (disc / mrp) * 100 : null;
  };

  // Pre-compute per-invoice effective discount excluding freebie SKUs for the filtered set.
  const effDiscExclMap = useMemo(() => {
    const m = new Map();
    filteredQuotations.forEach(q => { m.set(q.id, effDiscExclFreebies(q)); });
    return m;
  }, [filteredQuotations]);

  const discountByDate = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const d = q.invoice_date?.substring(0,10);
      const eff = effDiscExclMap.get(q.id);
      if (!d || eff == null) return;
      if (!m[d]) m[d] = { sum: 0, cnt: 0 };
      m[d].sum += eff; m[d].cnt++;
    });
    return Object.entries(m).sort(([a],[b])=>a.localeCompare(b)).map(([d,v])=>({date:d,value:v.cnt>0?v.sum/v.cnt:0,label:new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}));
  }, [filteredQuotations, effDiscExclMap]);

  // 6-bucket distribution per spec.
  const BUCKETS = [
    { name: "0–50%",   min: 0,   max: 50,  color: "#10b981" }, // green
    { name: "50–60%",  min: 50,  max: 60,  color: "#FBBF24" }, // light amber
    { name: "60–62%",  min: 60,  max: 62,  color: "#F59E0B" }, // amber
    { name: "62–64%",  min: 62,  max: 64,  color: "#D97706" }, // dark amber / orange
    { name: "64–65%",  min: 64,  max: 65,  color: "#F97316" }, // coral / orange-red
    { name: "66%+",    min: 66,  max: Infinity, color: "#DC2626" }, // red (most prominent)
  ];

  // Bucket a single discount value. Note: 65–66% falls into no bucket per the spec
  // (gap intentional), but we route values in [65, 66) into the 64–65 bucket to avoid
  // silently dropping invoices. Values ≥66 → red bucket.
  const bucketOf = (d) => {
    if (d >= 66) return BUCKETS[5];
    if (d >= 64) return d >= 65 ? BUCKETS[4] : BUCKETS[4];
    if (d >= 62) return BUCKETS[3];
    if (d >= 60) return BUCKETS[2];
    if (d >= 50) return BUCKETS[1];
    return BUCKETS[0];
  };

  const histogram = useMemo(() => {
    const counts = BUCKETS.map(b => ({ ...b, count: 0 }));
    filteredQuotations.forEach(q => {
      const d = effDiscExclMap.get(q.id);
      if (d == null) return;
      const b = bucketOf(d);
      const idx = BUCKETS.findIndex(x => x.name === b.name);
      if (idx >= 0) counts[idx].count++;
    });
    const total = counts.reduce((s, b) => s + b.count, 0);
    return counts.map(b => ({ ...b, pct: total > 0 ? (b.count / total) * 100 : 0 }));
  }, [filteredQuotations, effDiscExclMap]);

  // Visible brand columns: when user filtered to a subset of brands, show only those.
  const visibleBrands = useMemo(
    () => (selectedBrands.length > 0 ? selectedBrands.filter(b => ALL_BRANDS.includes(b)) : ALL_BRANDS),
    [selectedBrands]
  );

  // Helper: compute brand-level effective discount % within a single quotation.
  // Formula (matches backend effective_discount): Σ(line_mrp_sale + line_mrp_freebie - line_invoice_value) ÷ Σ(line_mrp_sale + line_mrp_freebie) × 100,
  // grouped by brand. Freebies are included (their invoice_value = 0) so over-discount
  // driven by freebies correctly inflates the brand %.
  // Returns { brand: pct } only for brands that have ≥ 1 line item (sale or freebie) in this quotation.
  const computeInvoiceBrandPct = (q) => {
    const totals = {};
    const accumulate = (it) => {
      const b = it.brand;
      if (!b) return;
      const qty = Number(it.quantity) || 0;
      const lineMrp = Number(it.total_mrp) || (Number(it.mrp) || 0) * qty;
      const lineInvoice = Number(it.invoice_value) || 0;
      if (!totals[b]) totals[b] = { mrp: 0, disc: 0 };
      totals[b].mrp += lineMrp;
      totals[b].disc += (lineMrp - lineInvoice);
    };
    (q.items || []).forEach(accumulate);
    (q.freebies || []).forEach(accumulate);
    const out = {};
    for (const b of Object.keys(totals)) {
      out[b] = totals[b].mrp > 0 ? (totals[b].disc / totals[b].mrp) * 100 : 0;
    }
    return out;
  };

  // ---- TABLE 1: Seller-wise — MRP-weighted avg per-brand discount across that seller's invoices.
  // For each (seller, brand) we accumulate Σ line_mrp and Σ (line_mrp - line_invoice_value) across
  // ALL eligible line items (sale + freebie list, EXCLUDING whitelisted freebie SKUs like Shakers,
  // Carry Bags, etc.) of that seller's filtered invoices. Cell value = Σdisc / Σmrp × 100.
  // Total Discount % = average of per-invoice effective_discount EXCLUDING freebie SKUs.
  const sellerDiscountRows = useMemo(() => {
    const groups = {};
    filteredQuotations.forEach(q => {
      const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!groups[name]) groups[name] = { name, count: 0, revenue: 0, brandTotals: {}, effSamples: [] };
      groups[name].count += 1;
      // Revenue excludes any freebie-SKU lines so it reflects only actual sale value.
      const saleRevenue = (q.items || []).reduce((s, it) => {
        if (isFreebieSkuId(it?.sku_id)) return s;
        return s + (Number(it?.invoice_value) || 0);
      }, 0);
      groups[name].revenue += saleRevenue;
      const eff = effDiscExclMap.get(q.id);
      if (eff != null) groups[name].effSamples.push(eff);
      const accumulate = (it) => {
        const b = it.brand;
        if (!b) return;
        if (isFreebieSkuId(it?.sku_id)) return;
        const qty = Number(it.quantity) || 0;
        const lineMrp = Number(it.total_mrp) || (Number(it.mrp) || 0) * qty;
        const lineInvoice = Number(it.invoice_value) || 0;
        if (!groups[name].brandTotals[b]) groups[name].brandTotals[b] = { mrp: 0, disc: 0 };
        groups[name].brandTotals[b].mrp += lineMrp;
        groups[name].brandTotals[b].disc += (lineMrp - lineInvoice);
      };
      (q.items || []).forEach(accumulate);
      (q.freebies || []).forEach(accumulate);
    });
    return Object.values(groups).map(g => {
      const brandAvg = {};
      for (const b of ALL_BRANDS) {
        const t = g.brandTotals[b];
        brandAvg[b] = t && t.mrp > 0 ? (t.disc / t.mrp) * 100 : null;
      }
      const totalDiscount = g.effSamples.length > 0
        ? g.effSamples.reduce((s, x) => s + x, 0) / g.effSamples.length
        : null;
      const anyHigh =
        ALL_BRANDS.some(b => brandAvg[b] != null && brandAvg[b] >= 62) ||
        (totalDiscount != null && totalDiscount >= 62);
      return { name: g.name, count: g.count, revenue: g.revenue, brandAvg, totalDiscount, anyHigh };
    });
  }, [filteredQuotations, sellerMap, effDiscExclMap]);

  // Default sort: Total Discount % DESC. Each brand column sorts on its own pct (nulls last).
  const sellerSortAccessors = useMemo(() => {
    const map = {
      name: (r) => r.name,
      count: (r) => r.count,
      revenue: (r) => r.revenue,
      totalDiscount: (r) => r.totalDiscount,
    };
    ALL_BRANDS.forEach(b => { map[b] = (r) => r.brandAvg[b]; });
    return map;
  }, []);
  const { sortedRows: sortedSellerRows, sort: sellerSort, toggleSort: toggleSellerSort } =
    useTableSort(sellerDiscountRows, { defaultKey: "totalDiscount", defaultOrder: "desc", accessors: sellerSortAccessors });



  // Reusable cell renderer for a brand-discount % value
  const renderBrandCell = (val, key) => {
    if (val == null) {
      return <TableCell key={key} className="text-xs text-right font-mono" style={{ color: "#888780", minWidth: 90 }}>—</TableCell>;
    }
    const c = cellColor(val);
    const warn = needsWarn(val);
    return (
      <TableCell key={key} className="text-xs text-right font-mono font-semibold" style={{ color: c, minWidth: 90 }}>
        {warn && <AlertTriangle className="inline h-3 w-3 mr-1" style={{ color: c }} />}{val.toFixed(1)}%
      </TableCell>
    );
  };

  // Brand-pill header label
  const BrandHeaderPill = ({ brand }) => {
    const p = BRAND_PILLS[brand];
    return (
      <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: p.bg, color: p.color }}>
        {brand} %
      </span>
    );
  };

  // Scatter plot data: per-seller avg effective discount EXCLUDING freebie SKUs.
  const sellerDiscount = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (!m[name]) m[name] = { name, discounts: [], revenue: 0 };
      const eff = effDiscExclMap.get(q.id);
      if (eff != null) m[name].discounts.push(eff);
      // Revenue excludes freebie-SKU lines so the scatter Y-axis reflects only actual sale value.
      const saleRevenue = (q.items || []).reduce((s, it) => {
        if (isFreebieSkuId(it?.sku_id)) return s;
        return s + (Number(it?.invoice_value) || 0);
      }, 0);
      m[name].revenue += saleRevenue;
    });
    return Object.values(m)
      .map(s => {
        const avg = s.discounts.length > 0 ? s.discounts.reduce((a, b) => a + b, 0) / s.discounts.length : 0;
        const b = bucketOf(avg);
        return { ...s, avgDiscount: avg, count: s.discounts.length, bucketName: b.name, bucketColor: b.color };
      })
      .sort((a, b) => b.avgDiscount - a.avgDiscount);
  }, [filteredQuotations, sellerMap, effDiscExclMap]);

  // ---- Brand-wise aggregate: Σ(line_mrp - invoice_value) ÷ Σ(line_mrp) per brand ----
  // Considers sale items + freebie-list lines, BUT excludes whitelisted freebie SKUs (Shakers,
  // Carry Bags, etc.) so they don't pollute the brand discount %. Revenue is from non-freebie
  // sale lines only. Invoice count = distinct quotations containing that brand on an eligible line.
  const brandWiseStats = useMemo(() => {
    const m = {};
    const invoiceSeen = {}; // brand -> Set of quotation ids (for distinct invoice count)
    filteredQuotations.forEach(q => {
      const accumulate = (it, isFreebie) => {
        const b = it.brand || "Unknown";
        if (selectedBrands.length > 0 && !selectedBrands.includes(b)) return;
        if (isFreebieSkuId(it?.sku_id)) return;
        const qty = Number(it.quantity) || 0;
        const lineMrp = Number(it.total_mrp) || (Number(it.mrp) || 0) * qty;
        const lineInvoice = Number(it.invoice_value) || 0;
        if (!m[b]) m[b] = { brand: b, mrp: 0, disc: 0, revenue: 0 };
        m[b].mrp += lineMrp;
        m[b].disc += (lineMrp - lineInvoice);
        if (!isFreebie) m[b].revenue += lineInvoice;
        if (!invoiceSeen[b]) invoiceSeen[b] = new Set();
        invoiceSeen[b].add(q.id);
      };
      (q.items || []).forEach(it => accumulate(it, false));
      (q.freebies || []).forEach(it => accumulate(it, true));
    });
    return Object.values(m).map(r => ({
      brand: r.brand,
      avgDiscount: r.mrp > 0 ? (r.disc / r.mrp) * 100 : 0,
      revenue: r.revenue,
      invoices: invoiceSeen[r.brand]?.size || 0,
    }));
  }, [filteredQuotations, selectedBrands]);

  const sortedBrandBars = useMemo(
    () => [...brandWiseStats].sort((a, b) => b.avgDiscount - a.avgDiscount),
    [brandWiseStats]
  );

  const brandTableSortAccessors = useMemo(() => ({
    brand: (r) => r.brand,
    avgDiscount: (r) => r.avgDiscount,
    revenue: (r) => r.revenue,
    invoices: (r) => r.invoices,
  }), []);
  const { sortedRows: sortedBrandTableRows, sort: brandTableSort, toggleSort: toggleBrandTableSort } =
    useTableSort(brandWiseStats, { defaultKey: "avgDiscount", defaultOrder: "desc", accessors: brandTableSortAccessors });

  // ---- SKU-wise aggregate ----
  // Excludes whitelisted freebie SKUs entirely — these are gifts (Shakers, Carry Bags, etc.)
  // not real sale SKUs, so they should not appear in SKU discount analytics.
  const skuWiseStats = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => {
      (q.items || []).forEach(it => {
        const b = it.brand || "Unknown";
        if (selectedBrands.length > 0 && !selectedBrands.includes(b)) return;
        if (isFreebieSkuId(it?.sku_id)) return;
        const skuKey = it.sku_id || it.product_id || it.product_name || "—";
        const qty = Number(it.quantity) || 0;
        const lineMrp = Number(it.total_mrp) || (Number(it.mrp) || 0) * qty;
        const lineInvoice = Number(it.invoice_value) || 0;
        if (!m[skuKey]) {
          m[skuKey] = {
            key: skuKey,
            sku_id: it.sku_id || it.item_code || "—",
            sku_name: it.product_name || "—",
            brand: b,
            mrp: 0, disc: 0, revenue: 0, qty: 0,
          };
        }
        m[skuKey].mrp += lineMrp;
        m[skuKey].disc += (lineMrp - lineInvoice);
        m[skuKey].revenue += lineInvoice;
        m[skuKey].qty += qty;
      });
    });
    return Object.values(m).map(r => ({
      ...r,
      avgDiscount: r.mrp > 0 ? (r.disc / r.mrp) * 100 : 0,
    }));
  }, [filteredQuotations, selectedBrands]);

  const skuSortAccessors = useMemo(() => ({
    sku_name: (r) => r.sku_name,
    sku_id: (r) => r.sku_id,
    brand: (r) => r.brand,
    avgDiscount: (r) => r.avgDiscount,
    revenue: (r) => r.revenue,
    qty: (r) => r.qty,
  }), []);
  const { sortedRows: sortedSkuRows, sort: skuSort, toggleSort: toggleSkuSort } =
    useTableSort(skuWiseStats, { defaultKey: "avgDiscount", defaultOrder: "desc", accessors: skuSortAccessors });

  // Modal SKU rows: search-filtered + sorted (separate sort so modal interactions don't bleed into top-10).
  const filteredSkuRowsForModal = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return skuWiseStats;
    return skuWiseStats.filter(r =>
      (r.sku_name || "").toLowerCase().includes(q) ||
      (r.sku_id || "").toLowerCase().includes(q)
    );
  }, [skuWiseStats, skuSearch]);
  const { sortedRows: sortedModalSkuRows, sort: modalSkuSort, toggleSort: toggleModalSkuSort } =
    useTableSort(filteredSkuRowsForModal, { defaultKey: "avgDiscount", defaultOrder: "desc", accessors: skuSortAccessors });

  // Brand pill (compact, used inline in tables/charts)
  const BrandPill = ({ brand }) => {
    const p = BRAND_PILLS[brand] || BRAND_PILLS["Unknown"];
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap" style={{ background: p.bg, color: p.color }}>
        {brand}
      </span>
    );
  };

  // Avg. Discount % cell with color coding (used in brand & SKU tables).
  const renderAvgDiscountCell = (val, key, fullPalette = false) => {
    if (val == null) {
      return <TableCell key={key} className="text-xs text-right font-mono" style={{ color: "#888780" }}>—</TableCell>;
    }
    // Brand table uses two-state palette per spec (green / amber+warn);
    // SKU table uses full 4-state palette (green / amber / coral / red).
    let c;
    if (fullPalette) c = cellColor(val);
    else c = val < 62 ? "#0F6E56" : "#BA7517";
    const warn = val >= 62;
    return (
      <TableCell key={key} className="text-xs text-right font-mono font-semibold" style={{ color: c }}>
        {warn && <AlertTriangle className="inline h-3 w-3 mr-1" style={{ color: c }} />}{val.toFixed(1)}%
      </TableCell>
    );
  };

  const FilterCheckItem = ({ checked, label, onClick }) => (
    <div className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${checked ? 'bg-accent/10' : ''}`} onClick={onClick}>
      <div className={`w-4 h-4 rounded border ${checked ? 'bg-accent border-accent' : 'border-slate-300'}`}>{checked && <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
      <span className="text-sm">{label}</span>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" /></div>;

  return (
    <div className="space-y-5">
      <div className="sticky top-0 z-40 bg-white pb-3 border-b border-[#E8E8E8] shadow-[0_2px_6px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-[#0F0F0F] mr-auto">Discount Analytics</h1>
          <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }} testId="discount-date-range" />
          <QuotationTypeToggle testId="discount-qtype" />
          <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
            <PopoverTrigger asChild><Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">{selectedBrands.length===0?"All Brands":`${selectedBrands.length} Brand${selectedBrands.length>1?"s":""}`}</Button></PopoverTrigger>
            <PopoverContent className="w-56 p-2"><div className="space-y-1">{ALL_BRANDS.map(b=><FilterCheckItem key={b} checked={selectedBrands.includes(b)} label={b} onClick={()=>toggleBrand(b)} />)}</div></PopoverContent>
          </Popover>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-red-500 hover:bg-red-50"><X className="h-3 w-3 mr-1" />Clear</Button>}
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8 px-2 border-[#E8E8E8]"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Avg. Discount % Trend</CardTitle><p className="text-xs text-muted-foreground">Red line = 62% threshold</p></CardHeader>
        <CardContent><div className="h-[280px]"><ResponsiveContainer width="100%" height="100%"><LineChart data={discountByDate} margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="label" tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={discountByDate.length>15?Math.floor(discountByDate.length/8):0}/><YAxis domain={[0,"auto"]} tick={{fontSize:10}} tickLine={false} axisLine={false} tickFormatter={v=>`${v.toFixed(0)}%`} width={50}/><ReferenceLine y={62} stroke="#ef4444" strokeDasharray="4 4" label={{value:"62% threshold",position:"right",fill:"#ef4444",fontSize:11}}/><Tooltip formatter={v=>[`${Number(v).toFixed(2)}%`,"Avg Discount"]} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}}/><Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer></div></CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Discount Distribution</CardTitle><p className="text-xs text-muted-foreground">Healthy → Critical zones</p></CardHeader>
          <CardContent><div className="h-[260px]"><ResponsiveContainer width="100%" height="100%"><BarChart data={histogram.filter(b=>b.min>=50)} margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="name" tick={{fontSize:11}} tickLine={false} axisLine={{stroke:"#E8E8E8"}}/><YAxis tick={{fontSize:10}} tickLine={false} axisLine={false}/><Tooltip formatter={(v, _n, ctx) => [`${v} invoices (${(ctx.payload.pct || 0).toFixed(1)}%)`, ctx.payload.name]} contentStyle={{borderRadius:8,fontSize:12}}/><Bar dataKey="count" radius={[4,4,0,0]} barSize={46} label={{ position:"top", fontSize: 10, fill:"#1A1A1A", formatter:(v)=>v>0?v:"" }}>{histogram.filter(b=>b.min>=50).map((b,i)=><Cell key={i} fill={b.color} />)}</Bar></BarChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Discount vs Revenue (by Seller)</CardTitle><p className="text-[11px] text-muted-foreground">Dot color = seller's avg. discount bucket</p></CardHeader>
          <CardContent><div className="h-[240px]"><ResponsiveContainer width="100%" height="100%"><ScatterChart margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis type="number" dataKey="avgDiscount" name="Avg. Discount %" domain={[50, 100]} tick={{fontSize:10}} tickLine={false} tickFormatter={v=>`${v.toFixed(0)}%`}/><YAxis type="number" dataKey="revenue" name="Revenue" tick={{fontSize:10}} tickLine={false} tickFormatter={formatCurrency} width={70}/><Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-white rounded-lg shadow-md p-2.5" style={{ border: "0.5px solid #E8E8E8" }}>
                  <p className="text-[12px] font-semibold text-[#0F0F0F] mb-1">{d.name}</p>
                  <div className="space-y-0.5 text-[11px]">
                    <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Revenue</span><span className="font-mono">{formatCurrency(d.revenue)}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Avg. Discount</span><span className="font-mono font-medium" style={{color: d.bucketColor}}>{d.avgDiscount.toFixed(2)}%</span></div>
                    <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Bucket</span><span className="font-medium" style={{color: d.bucketColor}}>{d.bucketName}</span></div>
                  </div>
                </div>
              );
            }}/><ReferenceLine x={62} stroke="#ef4444" strokeDasharray="4 4"/><Scatter data={sellerDiscount} fillOpacity={0.85}>{sellerDiscount.map((s,i)=><Cell key={i} fill={s.bucketColor}/>)}</Scatter></ScatterChart></ResponsiveContainer></div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 pt-2 border-t border-[#E8E8E8]">
            {BUCKETS.map(b => (
              <div key={b.name} className="flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />
                <span className="text-[10px] text-[#1A1A1A]">{b.name}</span>
              </div>
            ))}
          </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== Brand Wise Discount % ===== */}
      <Card data-testid="brand-wise-discount-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Brand Wise Discount %</CardTitle>
          <p className="text-[11px] text-muted-foreground">Average effective discount % by brand</p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Left: Horizontal bar chart */}
            <div>
              <div style={{ height: Math.max(180, sortedBrandBars.length * 44) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sortedBrandBars} layout="vertical" margin={{ top: 5, right: 50, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E8E8E8" }} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[0, "auto"]} />
                    <YAxis type="category" dataKey="brand" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={90} />
                    <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white rounded-lg shadow-md p-2.5" style={{ border: "0.5px solid #E8E8E8" }}>
                          <p className="text-[12px] font-semibold text-[#0F0F0F] mb-1">{d.brand}</p>
                          <div className="space-y-0.5 text-[11px]">
                            <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Avg. Discount</span><span className="font-mono font-medium" style={{ color: barColor(d.avgDiscount) }}>{d.avgDiscount.toFixed(2)}%</span></div>
                            <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Total Revenue</span><span className="font-mono">{formatCurrency(d.revenue)}</span></div>
                            <div className="flex justify-between gap-4"><span className="text-[#1A1A1A]/70">Invoices</span><span className="font-mono">{d.invoices}</span></div>
                          </div>
                        </div>
                      );
                    }} />
                    <ReferenceLine x={62} stroke="#ef4444" strokeDasharray="4 4" />
                    <Bar dataKey="avgDiscount" radius={[0, 4, 4, 0]} barSize={22} label={{ position: "right", fontSize: 11, fill: "#1A1A1A", formatter: (v) => `${Number(v).toFixed(1)}%` }}>
                      {sortedBrandBars.map((d, i) => <Cell key={i} fill={barColor(d.avgDiscount)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {sortedBrandBars.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No brand data for current filters.</p>}
            </div>

            {/* Right: Brand Performance table */}
            <div>
              <p className="text-sm font-semibold text-[#0F0F0F] mb-2">Brand Performance by Discount</p>
              <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <SortableHeader sortKey="brand" sort={brandTableSort} onToggle={toggleBrandTableSort} className="text-xs">Brand</SortableHeader>
                      <SortableHeader sortKey="avgDiscount" sort={brandTableSort} onToggle={toggleBrandTableSort} align="right" className="text-xs">Avg. Discount %</SortableHeader>
                      <SortableHeader sortKey="revenue" sort={brandTableSort} onToggle={toggleBrandTableSort} align="right" className="text-xs">Total Revenue</SortableHeader>
                      <SortableHeader sortKey="invoices" sort={brandTableSort} onToggle={toggleBrandTableSort} align="right" className="text-xs">No. of Invoices</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedBrandTableRows.map((r) => (
                      <TableRow key={r.brand} data-testid={`brand-row-${r.brand}`}>
                        <TableCell className="text-xs"><BrandPill brand={r.brand} /></TableCell>
                        {renderAvgDiscountCell(r.avgDiscount, `${r.brand}-pct`, false)}
                        <TableCell className="text-xs text-right font-mono">{formatCurrency(r.revenue)}</TableCell>
                        <TableCell className="text-xs text-right">{r.invoices}</TableCell>
                      </TableRow>
                    ))}
                    {sortedBrandTableRows.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">No brand data for current filters.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== SKU Wise Discount % ===== */}
      <Card data-testid="sku-wise-discount-section">
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base font-semibold">SKU Wise Discount %</CardTitle>
            <p className="text-[11px] text-muted-foreground">Average effective discount % by SKU</p>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="sku-count-label">
            Showing top {Math.min(10, sortedSkuRows.length)} of {sortedSkuRows.length} SKUs
          </p>
        </CardHeader>
        <CardContent>
          <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs w-10">#</TableHead>
                  <SortableHeader sortKey="sku_name" sort={skuSort} onToggle={toggleSkuSort} className="text-xs">SKU Name</SortableHeader>
                  <SortableHeader sortKey="sku_id" sort={skuSort} onToggle={toggleSkuSort} className="text-xs">SKU ID</SortableHeader>
                  <SortableHeader sortKey="brand" sort={skuSort} onToggle={toggleSkuSort} className="text-xs">Brand</SortableHeader>
                  <SortableHeader sortKey="avgDiscount" sort={skuSort} onToggle={toggleSkuSort} align="right" className="text-xs">Avg. Discount %</SortableHeader>
                  <SortableHeader sortKey="revenue" sort={skuSort} onToggle={toggleSkuSort} align="right" className="text-xs">Total Revenue</SortableHeader>
                  <SortableHeader sortKey="qty" sort={skuSort} onToggle={toggleSkuSort} align="right" className="text-xs">Qty Sold</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSkuRows.slice(0, 10).map((r, i) => (
                  <TableRow key={r.key} data-testid={`sku-row-${i}`}>
                    <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="text-xs font-medium">{r.sku_name}</TableCell>
                    <TableCell className="text-xs font-mono text-[#1A1A1A]/80">{r.sku_id}</TableCell>
                    <TableCell className="text-xs"><BrandPill brand={r.brand} /></TableCell>
                    {renderAvgDiscountCell(r.avgDiscount, `${r.key}-pct`, true)}
                    <TableCell className="text-xs text-right font-mono">{formatCurrency(r.revenue)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.qty.toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                ))}
                {sortedSkuRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">No SKU data for current filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {sortedSkuRows.length > 10 && (
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllSkus(true)}
                className="text-xs font-medium hover:bg-[#32B4AC]/10"
                style={{ color: "#32B4AC" }}
                data-testid="view-all-skus-btn"
              >
                View All SKUs →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Seller-wise Discount</CardTitle><p className="text-[11px] text-muted-foreground">Amber row = any brand or total ≥ 62% threshold</p></CardHeader>
        <CardContent><div className="max-h-[400px] overflow-auto"><Table><TableHeader><TableRow className="bg-slate-50">
          <SortableHeader sortKey="name" sort={sellerSort} onToggle={toggleSellerSort} className="text-xs">Seller</SortableHeader>
          {visibleBrands.map(b => (
            <SortableHeader key={b} sortKey={b} sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs" style={{ minWidth: 90 }}>
              <BrandHeaderPill brand={b} />
            </SortableHeader>
          ))}
          <SortableHeader sortKey="totalDiscount" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs font-bold" style={{ minWidth: 110, borderLeft: "1px solid #E8E8E8", background: "#F5F4F0" }}>
            Total Discount %
          </SortableHeader>
          <SortableHeader sortKey="count" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Orders</SortableHeader>
          <SortableHeader sortKey="revenue" sort={sellerSort} onToggle={toggleSellerSort} align="right" className="text-xs">Revenue</SortableHeader>
        </TableRow></TableHeader><TableBody>
          {sortedSellerRows.map((s, i) => {
            const td = s.totalDiscount;
            const tdColor = cellColor(td);
            const tdWarn = needsWarn(td);
            return (
              <TableRow key={i} className={s.anyHigh ? "bg-[#FAEEDA]/30" : ""}>
                <TableCell className="text-xs">{s.name}</TableCell>
                {visibleBrands.map(b => renderBrandCell(s.brandAvg[b], b))}
                <TableCell
                  className="text-xs text-right font-mono font-semibold"
                  style={{
                    color: td == null ? "#888780" : tdColor,
                    minWidth: 110,
                    borderLeft: "1px solid #E8E8E8",
                    background: s.anyHigh ? undefined : "#F5F4F0",
                  }}
                  data-testid={`seller-total-discount-${i}`}
                >
                  {td == null ? "—" : (
                    <>
                      {tdWarn && <AlertTriangle className="inline h-3 w-3 mr-1" style={{ color: tdColor }} />}
                      {td.toFixed(1)}%
                    </>
                  )}
                </TableCell>
                <TableCell className="text-xs text-right">{s.count}</TableCell>
                <TableCell className="text-xs text-right font-mono">{formatCurrency(s.revenue)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody></Table></div></CardContent>
      </Card>

      {/* ===== Modal: All SKUs ===== */}
      <Dialog open={showAllSkus} onOpenChange={setShowAllSkus}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[85vh] flex flex-col p-0 overflow-hidden" data-testid="all-skus-modal">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#E8E8E8]">
            <DialogTitle className="text-base font-semibold text-[#0F0F0F]">All SKUs — Discount Analysis</DialogTitle>
            <p className="text-[11px] text-muted-foreground" data-testid="modal-sku-count-label">
              Showing all {sortedModalSkuRows.length} SKU{sortedModalSkuRows.length === 1 ? "" : "s"}
            </p>
          </DialogHeader>
          <div className="px-5 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search by SKU name or SKU ID…"
                value={skuSearch}
                onChange={(e) => setSkuSearch(e.target.value)}
                className="h-8 pl-8 text-xs border-[#E8E8E8]"
                data-testid="modal-sku-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-auto px-5 pb-5">
            <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50 z-10">
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs w-10">#</TableHead>
                    <SortableHeader sortKey="sku_name" sort={modalSkuSort} onToggle={toggleModalSkuSort} className="text-xs">SKU Name</SortableHeader>
                    <SortableHeader sortKey="sku_id" sort={modalSkuSort} onToggle={toggleModalSkuSort} className="text-xs">SKU ID</SortableHeader>
                    <SortableHeader sortKey="brand" sort={modalSkuSort} onToggle={toggleModalSkuSort} className="text-xs">Brand</SortableHeader>
                    <SortableHeader sortKey="avgDiscount" sort={modalSkuSort} onToggle={toggleModalSkuSort} align="right" className="text-xs">Avg. Discount %</SortableHeader>
                    <SortableHeader sortKey="revenue" sort={modalSkuSort} onToggle={toggleModalSkuSort} align="right" className="text-xs">Total Revenue</SortableHeader>
                    <SortableHeader sortKey="qty" sort={modalSkuSort} onToggle={toggleModalSkuSort} align="right" className="text-xs">Qty Sold</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedModalSkuRows.map((r, i) => (
                    <TableRow key={r.key}>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-xs font-medium">{r.sku_name}</TableCell>
                      <TableCell className="text-xs font-mono text-[#1A1A1A]/80">{r.sku_id}</TableCell>
                      <TableCell className="text-xs"><BrandPill brand={r.brand} /></TableCell>
                      {renderAvgDiscountCell(r.avgDiscount, `${r.key}-modal-pct`, true)}
                      <TableCell className="text-xs text-right font-mono">{formatCurrency(r.revenue)}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{r.qty.toLocaleString("en-IN")}</TableCell>
                    </TableRow>
                  ))}
                  {sortedModalSkuRows.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">No SKUs match your search.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
